import { useEffect } from "react";

import { Button, Text, VisuallyHidden } from "@vericus/cadmus-ui";

import { captureMessage } from "@sentry/react";

import { Notification, NotificationTone } from "@/generated/graphql";

import * as styles from "./action-card.css";

export interface ActionCardProps {
  action: Notification;
  onAct: (action: Notification) => void;
  disabled?: boolean;
}

/**
 * One student-owned action rendered as its own card. It is deliberately
 * router-free: a click hands the whole `action` back via `onAct` instead of
 * navigating, so routing (`action-routes.ts`) and click analytics stay
 * `ActionFeed`'s responsibility and this card stays reusable without either
 * dependency.
 */
export function ActionCard({
  action: notification,
  onAct,
  disabled,
}: ActionCardProps) {
  // Aliased from the `action` prop to `notification` here only, because the
  // schema's own nested `action.label` (the button text) would otherwise
  // have to be read as `action.action?.label`, which reads as a typo. The
  // exported prop name stays `action`: this component's own call sites and
  // fixtures name it that way, and nothing outside this function body needs
  // the alias.
  const actionLabel = notification.action?.label;

  const tone = notification.tone;
  const variant = toneVariant(tone);

  // toneVariant is a pure mapping; reporting is a side effect, so it lives
  // here instead, keyed on `tone` so it fires once per distinct value.
  useEffect(() => {
    if (isUnknownTone(tone)) {
      captureMessage(`Unknown notification tone: ${String(tone)}`);
    }
  }, [tone]);

  return (
    <div className={styles.card}>
      <span
        className={styles.toneDot[variant]}
        data-testid="action-tone"
        data-tone={tone}
      >
        <VisuallyHidden.Root>{toneLabel(variant)}</VisuallyHidden.Root>
      </span>
      <div className={styles.body}>
        <Text kind="label" as="div" color="indigo500" bold>
          Your action
        </Text>
        <Text kind="headingFour">{notification.title}</Text>
        <Text kind="bodySm" color="grey500">
          {notification.detail}
        </Text>
      </div>
      {/* A notification reaching this component with no `action` (hence no
          label) is a caller bug: every entry in `StudentActionFeed.actions` is
          documented as something the student can act on. Rendering no button
          here, rather than an empty one, keeps that bug visible instead of
          hiding it behind a dead click target. */}
      {actionLabel != null && (
        <Button
          kind="secondary"
          onClick={() => onAct(notification)}
          disabled={disabled}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

const KNOWN_TONES: ReadonlySet<Notification["tone"]> = new Set([
  NotificationTone.Red,
  NotificationTone.Amber,
  NotificationTone.Grey,
  null,
]);

// True only for a schema value this build doesn't recognise yet. The
// documented `null` case below (waiting notes) is expected, not unknown.
function isUnknownTone(tone: Notification["tone"]): boolean {
  return !KNOWN_TONES.has(tone);
}

// Pure mapping: no Sentry reporting here, so it is safe to call more than
// once per render. See the component's effect for the unknown-tone report.
function toneVariant(tone: Notification["tone"]): keyof typeof styles.toneDot {
  switch (tone) {
    case NotificationTone.Red:
      return "red";
    case NotificationTone.Amber:
      return "amber";
    case NotificationTone.Grey:
      return "grey";
    case null:
      // `tone` is schema-nullable only because `actions` and `waitingOnOthers`
      // share one `Notification` type: a waiting note carries no tone. This
      // component only ever renders entries from `actions` (action-feed.tsx),
      // which are contractually tone-bearing, so reaching this case is a
      // caller bug, the same category as a missing `action`. It falls back
      // to the least alarming tone rather than crashing the whole feed over
      // it.
      return "grey";
    default:
      // A schema addition this build doesn't know about yet (backend ships
      // ahead of frontend) must degrade, not take the whole feed down: fall
      // back to the least alarming tone instead of throwing.
      return "grey";
  }
}

// Stated in words for assistive technology, since the dot otherwise
// conveys urgency by colour alone. Takes the already-computed variant so
// wording and colour can't drift apart, and `toneVariant` isn't re-invoked.
function toneLabel(variant: keyof typeof styles.toneDot): string {
  return `${variant.charAt(0).toUpperCase()}${variant.slice(1)} priority`;
}
